import {
  addMoney,
  amountScale,
  compareMoney,
  type CreateTransactionRequest,
  createTransactionRequestSchema,
  isNegativeMoney,
  isZeroMoney,
  parseAmountInput,
  parseRateInput,
  type RecordableKind,
  subtractMoney,
  type Transaction,
} from '@familywallet/shared';
import { z } from 'zod';
import { groupAmountInput } from './money';

/** What the Add screen's form holds: everything as typed, parsed only when saving. */
export interface AddFormValues {
  amount: string;
  currency: string;
  accountId: string;
  fromAccountId: string;
  toAccountId: string;
  categoryId: string;
  split: boolean;
  lines: { categoryId: string; amount: string; note: string }[];
  payeeName: string;
  occurredOn: string;
  description: string;
  sharedWithFamily: boolean;
  rate: string;
  /** Part on credit: what was paid now (may be 0), and the person who is owed / owes the rest. */
  credit: boolean;
  paidNow: string;
  personId: string;
  personName: string;
  dueOn: string;
}

export interface AddFormContext {
  kind: RecordableKind;
  /** True when the screen shows the one-off rate field (it must then hold a valid rate). */
  askRate: boolean;
}

/** A typed amount as a positive money string in the currency, or null. */
export function parsePositive(raw: string, currency: string): string | null {
  const amount = parseAmountInput(raw, currency);
  return amount && !isZeroMoney(amount) && !isNegativeMoney(amount) ? amount : null;
}

/** A typed amount that may be zero ("Paid now"), or null. */
export function parseNonNegative(raw: string, currency: string): string | null {
  const amount = parseAmountInput(raw, currency);
  return amount && !isNegativeMoney(amount) ? amount : null;
}

/** Part on credit: the total minus what was paid now, when both are valid and it is not negative. */
export function creditRemaining(
  values: Pick<AddFormValues, 'amount' | 'paidNow'>,
  currency: string,
): string | null {
  const total = parsePositive(values.amount, currency);
  const paid = parseNonNegative(values.paidNow, currency);
  if (!total || !paid) return null;
  const rest = subtractMoney(total, paid);
  return isNegativeMoney(rest) ? null : rest;
}

/** Why a typed amount is not valid: whole-number currencies get their own message. */
function amountError(raw: string, currency: string): string {
  if (raw.trim() === '') return 'add.errors.amountRequired';
  const scale = amountScale(currency);
  return scale === 0 && parsePositive(raw, 'USD') ? 'add.errors.amountWhole' : 'add.errors.amount';
}

/**
 * For a split: the total minus the lines typed so far ("Remaining: 12.50"). Null when the total
 * or a typed line is not a valid amount.
 */
export function splitRemaining(values: Pick<AddFormValues, 'amount' | 'lines'>, currency: string) {
  const total = parsePositive(values.amount, currency);
  // A line not typed yet counts as nothing, so "Remaining" shows from the first line on.
  const lines = values.lines
    .filter((l) => l.amount.trim() !== '')
    .map((l) => parsePositive(l.amount, currency));
  if (!total || lines.some((l) => l === null)) return null;
  return subtractMoney(total, addMoney('0', ...(lines as string[])));
}

interface Issue {
  path: (string | number)[];
  message: string;
}

/** Every problem with the form, as i18n keys on the field they belong to. */
export function checkAddForm(v: AddFormValues, ctx: AddFormContext): Issue[] {
  const issues: Issue[] = [];
  const amount = parsePositive(v.amount, v.currency);
  if (!amount) issues.push({ path: ['amount'], message: amountError(v.amount, v.currency) });
  if (!v.occurredOn) issues.push({ path: ['occurredOn'], message: 'add.errors.date' });
  if (ctx.askRate && !parseRateInput(v.rate)) {
    issues.push({ path: ['rate'], message: 'add.errors.rate' });
  }
  if (ctx.kind === 'transfer') {
    if (!v.fromAccountId) issues.push({ path: ['fromAccountId'], message: 'add.errors.account' });
    if (!v.toAccountId) issues.push({ path: ['toAccountId'], message: 'add.errors.account' });
    if (v.fromAccountId && v.fromAccountId === v.toAccountId) {
      issues.push({ path: ['toAccountId'], message: 'add.errors.sameAccount' });
    }
    return issues;
  }
  if (!v.accountId) issues.push({ path: ['accountId'], message: 'add.errors.account' });
  if (v.credit) {
    const paid = parseNonNegative(v.paidNow, v.currency);
    if (!paid) {
      issues.push({
        path: ['paidNow'],
        message:
          v.paidNow.trim() === '' ? 'credit.errors.paidNow' : amountError(v.paidNow, v.currency),
      });
    } else if (amount && compareMoney(paid, amount) > 0) {
      issues.push({ path: ['paidNow'], message: 'credit.errors.tooMuch' });
    } else if (amount && compareMoney(paid, amount) < 0 && !v.personId && !v.personName) {
      issues.push({ path: ['personId'], message: 'credit.errors.person' });
    }
  }
  if (!v.split) {
    if (!v.categoryId) issues.push({ path: ['categoryId'], message: 'add.errors.category' });
    return issues;
  }
  v.lines.forEach((line, i) => {
    if (!line.categoryId) {
      issues.push({ path: ['lines', i, 'categoryId'], message: 'add.errors.category' });
    }
    if (!parsePositive(line.amount, v.currency)) {
      issues.push({ path: ['lines', i, 'amount'], message: amountError(line.amount, v.currency) });
    }
  });
  const remaining = splitRemaining(v, v.currency);
  if (amount && remaining !== null && !isZeroMoney(remaining)) {
    issues.push({ path: ['lines'], message: 'add.errors.remaining' });
  }
  return issues;
}

/** The request for POST /transactions, checked by the shared schema the API uses. */
export function toRequest(v: AddFormValues, ctx: AddFormContext): CreateTransactionRequest {
  const amount = parsePositive(v.amount, v.currency)!;
  const common = {
    currency: v.currency,
    amount,
    occurredOn: v.occurredOn,
    ...(v.description.trim() ? { description: v.description.trim() } : {}),
    ...(ctx.askRate ? { rate: parseRateInput(v.rate)! } : {}),
  };
  const request: CreateTransactionRequest =
    ctx.kind === 'transfer'
      ? {
          ...common,
          kind: 'transfer',
          fromAccountId: v.fromAccountId,
          toAccountId: v.toAccountId,
        }
      : {
          ...common,
          kind: ctx.kind,
          accountId: v.accountId,
          lines: v.split
            ? v.lines.map((l) => ({
                categoryId: l.categoryId,
                amount: parsePositive(l.amount, v.currency)!,
                ...(l.note.trim() ? { note: l.note.trim() } : {}),
              }))
            : [{ categoryId: v.categoryId, amount }],
          ...(v.payeeName.trim() ? { payeeName: v.payeeName.trim() } : {}),
          sharedWithFamily: v.sharedWithFamily,
          ...(v.credit
            ? {
                credit: {
                  paidNow: parseNonNegative(v.paidNow, v.currency)!,
                  ...(v.personId
                    ? { personId: v.personId }
                    : v.personName
                      ? { personName: v.personName }
                      : {}),
                  ...(v.dueOn ? { dueOn: v.dueOn } : {}),
                },
              }
            : {}),
        };
  return createTransactionRequestSchema.parse(request) as CreateTransactionRequest;
}

/** react-hook-form schema: the checks above, then the API request. */
export function addFormSchema(ctx: AddFormContext) {
  return z
    .custom<AddFormValues>()
    .superRefine((v, c) => {
      for (const issue of checkAddForm(v, ctx)) {
        c.addIssue({ code: 'custom', path: issue.path, message: issue.message });
      }
    })
    .transform((v) => toRequest(v, ctx));
}

/**
 * Correct: what an expense or income fills the Add form with (amounts as typed, positive), its
 * money account and currency, and the credit part's person.
 */
export function fromTransaction(tx: Transaction): {
  accountId: string | null;
  currency: string;
  personLabel: string;
  values: Partial<AddFormValues>;
} {
  const positive = (v: string) => groupAmountInput(v.startsWith('-') ? v.slice(1) : v, tx.currency);
  const categories = tx.lines.filter((l) => l.role === 'category' && l.accountId && l.amount);
  const money = tx.lines.find((l) => l.role === 'money' && !l.masked);
  const split = categories.length > 1;
  return {
    accountId: money?.accountId ?? null,
    currency: tx.currency,
    personLabel: tx.credit?.personName ?? '',
    values: {
      amount: tx.amount ? positive(tx.amount) : '',
      currency: tx.currency,
      categoryId: split ? '' : (categories[0]?.accountId ?? ''),
      split,
      lines: split
        ? categories.map((l) => ({
            categoryId: l.accountId!,
            amount: positive(l.amount!),
            note: l.note ?? '',
          }))
        : [],
      payeeName: tx.payee?.name ?? '',
      occurredOn: tx.occurredOn,
      description: tx.description,
      sharedWithFamily: tx.sharedWithFamily,
      ...(tx.credit
        ? {
            credit: true,
            paidNow: tx.credit.paidNow === null ? '' : positive(tx.credit.paidNow),
            personId: tx.credit.personId,
            dueOn: tx.credit.dueOn ?? '',
          }
        : {}),
    },
  };
}
